import { MODULE_TABLE } from '@app/app.config'
import { TOASTIFY_DEFAULT_OPTIONS } from '@app/app.constants'
import { useAppSelector } from '@app/app.hook'
import { getErrorText } from '@app/app.method'
import NewDatePicker from '@app/components/NewDatePicker'
import { selectActiveModules, selectStrings } from '@app/slices/slice.app'
import { selectToken } from '@app/slices/slice.token'
import { TokenData } from '@app/types/type.token'
import { useRevalidateToken } from '@login/MutationProvider/revalidateToken'
import { useValidateAPIPath } from '@login/MutationProvider/validateAPIPath'

import { useGetPatientChartDataMutation } from '@doc/api'
import LineChart from '@doc/components/patients/details/charts/LineChart/LineChart'
import { IDS } from '@doc/constants'
import {
    GraphInfo,
    HealthRelatedReducerActions,
    HealthRelatedReducerState,
    PatientParams
} from '@doc/type'
import {
    FloatingArrow,
    FloatingFocusManager,
    arrow,
    autoUpdate,
    flip,
    offset,
    shift,
    useFloating,
    useHover,
    useId,
    useInteractions,
    useTransitionStyles
} from '@floating-ui/react'
import { getUnixTime } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

const InformationPopover = ({ text }:
    { text:string | undefined}) => {
    const arrowRef = useRef(null)
    const [visible, setVisible] = useState(false)

    const { refs, floatingStyles, context } = useFloating({
        open: visible,
        placement: 'top',
        onOpenChange: (isOpen, event, reason) => {
            setVisible(isOpen)
            // event && console.log(event)
            // reason && console.log(reason)
        },
        middleware: [
            offset(10),
            flip({
                fallbackAxisSideDirection: 'end',
                boundary: 'clippingAncestors'
            }),
            shift(),
            arrow({
                element: arrowRef
            })
        ],
        whileElementsMounted: autoUpdate

    })
    const { styles } = useTransitionStyles(context)

    const hover = useHover(context)

    const { getReferenceProps, getFloatingProps } = useInteractions([
        hover
    ])

    const headingId = useId()

    return <>
        <div className={'icon d-inline-block ps-2'}
            ref={refs.setReference} {...getReferenceProps()}>
            <i className={'fa-light fa-circle-info'}
                aria-hidden={'true'}></i>
        </div>
        { visible && <FloatingFocusManager context={context} modal={false}>
            <div
                className={'floating-ui information'}
                ref={refs.setFloating}
                style={{
                    ...floatingStyles,
                    ...styles
                }}
                aria-labelledby={headingId}
                {...getFloatingProps()}
            >
                <FloatingArrow ref={arrowRef} context={context}
                />
                {

                    <div><span>{
                        text
                    }</span></div>
                }
            </div>

        </FloatingFocusManager>}

    </>
}
interface ComponentProps {
    level: 'main' | 'primary'
    parentChartInfo: GraphInfo,
    detailsReducerState: HealthRelatedReducerState
    detailsReducerDispatch:React.Dispatch<HealthRelatedReducerActions>
    dateStates: {
        startDate: Date | null,
        endDate: Date | null
    }
    getSecondaryDataIsLoading?: boolean
}

const DetailedHealthRelatedChartInterface = ({
    level, parentChartInfo,
    detailsReducerState,
    detailsReducerDispatch,
    dateStates,
    getSecondaryDataIsLoading
} : ComponentProps) => {
    const revalidateToken = useRevalidateToken()
    const validateAPIPath = useValidateAPIPath()
    const strings = useAppSelector(selectStrings)
    const activeModules = useAppSelector(selectActiveModules)
    const token = useAppSelector(selectToken)

    const { userId } = useParams<PatientParams>()

    const [getPatientChartData, getPatientChartDataMutation] = useGetPatientChartDataMutation()

    const patientChartData = getPatientChartDataMutation.data
        ?.data

    const [startDate, setStartDate] = useState<Date | null>(dateStates.startDate)
    const [endDate, setEndDate] = useState<Date | null>(dateStates.endDate)

    /** create fetch data function */
    const fetchData = (token: TokenData) => {
        let isMounted = true

        const call = async () => {
            if (
                token.valid &&
                parentChartInfo.graphId &&
                userId && startDate && endDate
            ) {
                const newToken = await revalidateToken({
                    value: token.value,
                    id: token.id
                }, token.mode)
                if (isMounted) {
                    const isValid = validateAPIPath(
                        activeModules.arr,
                        MODULE_TABLE.doc.moduleName,
                        MODULE_TABLE.doc.apiPaths
                            .getPatientChartData.path,
                        true
                    )

                    if (isValid && newToken.value) {
                        getPatientChartData({
                            authToken: newToken.value,
                            data: {
                                dateFrom: getUnixTime(startDate),
                                dateTo: getUnixTime(endDate),
                                userId,
                                graphId: parentChartInfo.graphId
                            }
                        })
                    }
                }
            }
        }

        call()

        return () => {
            isMounted = false
        }
    }

    useEffect(() => {
        if (userId) {
            return fetchData(token)
        } else {
            return () => {}
        }
    }, [token.id, token.valid])

    useEffect(() => {
        if (getPatientChartDataMutation.error) {
            const message = getErrorText(getPatientChartDataMutation.error)
            console.error(message)
            toast.error(message, { ...TOASTIFY_DEFAULT_OPTIONS })
        }
    }, [getPatientChartDataMutation.error])

    const chartRangeId = [
        IDS.CHART.RANGE,
        '-',
        parentChartInfo.graphId
    ].join(' ')

    const IconPicker = <NewDatePicker
        id={chartRangeId}
        singleDate={startDate}
        startDate={startDate}
        endDate={endDate}
        selectsRange={true}
        isRange={(dates) => {
            const [start, end] = dates
            setStartDate(start)
            setEndDate(end)
        }}

    />

    const header = <div className={'row justify-content-between mb-4 align-items-start g-2'}>
        <div className={'col-auto'}>
            <h5 className={'mb-0 fw-semibold d-inline-block'} >{
                patientChartData?.graphName
            }</h5>
            <InformationPopover text={patientChartData?.graphDescription} />
        </div>
        <div className={'col-auto'}>
            <div className={'row g-3'}>
                <div className={'col-auto'}>
                    {/* refresh button */}
                    <div className={'icon d-inline-block'} onClick={() => {
                        if (getPatientChartDataMutation.isLoading === false) {
                            fetchData(token)
                        }
                    }}>
                        <i className={'fa-light fa-rotate-right'} aria-hidden={'true'}></i>
                    </div>
                </div>
                <div className={'col-auto'}>
                    {/* date range picker */}
                    {IconPicker}
                </div>
            </div>
        </div>
    </div>

    const chart = <div className={'chart-container'}>
        {
            getSecondaryDataIsLoading && <div className={'overlay rounded-5'}>
                <div className={'card px-2 py-2'}>{
                    strings.doc?.message.loading?.getting_detailed_chart_info || ''
                }</div>
            </div>
        }
        {
            patientChartData?.graphType === 'line'
                ? <LineChart
                    level={level}
                    detailsReducerState={detailsReducerState}
                    detailsReducerDispatch={detailsReducerDispatch}
                    patientChartData={patientChartData}
                    parentChartInfo={parentChartInfo}
                />
                : ''
        }

    </div>

    const isLoadingContent = <small className={'d-block text-center py-2'}>
        <div className={'spinner-container'}>
            <span className={'spinner-border spinner-border-sm'}></span>
            <span className={'ms-2'}>{
                strings.app?.text.loading || ''
            }</span>
        </div>
    </small>

    const content = <>
        <div className={'flex-grow-1'}>{header}</div>
        <div className={'flex-grow-0'}>{chart}</div>
    </>

    return <>
        <div className={'course-interface h-100 d-flex flex-column justify-content-center'}>
            {
                getPatientChartDataMutation.isLoading
                    ? isLoadingContent
                    : patientChartData
                        ? content
                        : ''
            }
        </div>
    </>
}

export default DetailedHealthRelatedChartInterface
